import { useState } from 'react';
import { useInventory, VIEW_MODES, SORT_KEYS, UNITS } from '../../contexts/InventoryContext';
import FilterRail from './FilterRail';
import ColumnPicker from './ColumnPicker';
import { api } from '../../api';

const SORT_LABELS = {
  [SORT_KEYS.FILENAME]: 'Name',
  [SORT_KEYS.DATE]: 'Date',
  [SORT_KEYS.EXTENT]: 'Length',
  [SORT_KEYS.SIZE]: 'Size',
  [SORT_KEYS.COST]: 'Cost',
  [SORT_KEYS.TYPE]: 'Type',
  [SORT_KEYS.STATUS]: 'Status',
};

/**
 * InventoryToolbar — search, status filter, type filter, sort, view mode.
 *
 * Props:
 *   uniqueTypes  — string[] of canonical types present in the dataset
 *   totalCount   — number, total documents after filtering
 *   visibleDocIds — ids currently visible in table (page or grouped scope)
 *   filteredDocIds — ids in the full filtered result set
 */
export default function InventoryToolbar({
  unit = UNITS.DOCUMENTS,
  totalCount = 0,
  visibleDocIds = [],
  filteredDocIds = [],
  duplicateGroupCount = 0,
  pendingCorrectionCount = 0,
  hasPendingPropagation = false,
  hasChecklist = false,
  checklistDocMap = {},
  caseId,
  documents = [],
  docsForFacets,
  onDocChange,
  onPropagate,
}) {
  const { dispatch, ...state } = useInventory();
  const [selectDropdownOpen, setSelectDropdownOpen] = useState(false);
  const [dedupOpen, setDedupOpen] = useState(false);
  const [dedupStrategy, setDedupStrategy] = useState('keep_oldest');
  const [dedupPreserveNames, setDedupPreserveNames] = useState(false);
  const [dedupBusy, setDedupBusy] = useState(false);
  const [dedupStatus, setDedupStatus] = useState(null);
  const selectedCount = state.selected.size;
  const allVisibleSelected = visibleDocIds.length > 0
    && visibleDocIds.every(id => state.selected.has(id));
  const allFilteredSelected = filteredDocIds.length > 0
    && filteredDocIds.every(id => state.selected.has(id));
  const canEscalateToFiltered = state.viewMode === VIEW_MODES.LIST
    && allVisibleSelected
    && filteredDocIds.length > visibleDocIds.length
    && !allFilteredSelected;

  return (
    <div className="inv-toolbar">
      {/* Search */}
      <div className="inv-toolbar-search">
        <input
          type="text"
          placeholder={unit === UNITS.FILES ? 'Search files…' : 'Search documents…'}
          value={state.searchText}
          onChange={e => dispatch({ type: 'SET_SEARCH', text: e.target.value })}
          className="inv-input"
        />
        {state.searchText && (
          <button
            className="inv-input-clear"
            onClick={() => dispatch({ type: 'SET_SEARCH', text: '' })}
            aria-label="Clear search"
          >
            ✕
          </button>
        )}
      </div>

      {/* Filter rail — persistent chips + add-filter picker */}
      <FilterRail
        filters={state.filters || []}
        documents={documents}
        docsForFacets={docsForFacets || documents}
        ctx={{ hasChecklist, checklistDocMap, hasRedactedDocs: documents.some(d => d.is_redacted), hasDerivedDocs: documents.some(d => d.is_derived) }}
        onAdd={({ axis, value }) => dispatch({ type: 'ADD_FILTER', axis, value })}
        onRemove={(axis, value) => dispatch({ type: 'REMOVE_FILTER', axis, value })}
        onClearAll={() => dispatch({ type: 'RESET_FILTERS' })}
      />

      {/* Duplicates filter */}
      {duplicateGroupCount > 0 && (
        <div className="inv-dedup-group">
          <button
            className={`inv-dup-filter-btn ${state.duplicateFilter ? 'inv-dup-filter-btn--active' : ''}`}
            onClick={() => dispatch({ type: 'SET_DUPLICATE_FILTER' })}
            title={state.duplicateFilter ? 'Show all documents' : `Show ${duplicateGroupCount} duplicate group${duplicateGroupCount !== 1 ? 's' : ''}`}
          >
            🔁 Duplicates{state.duplicateFilter ? ' ✓' : ''}
          </button>
          <button
            className={`inv-dup-filter-btn inv-dedup-btn ${dedupOpen ? 'inv-dup-filter-btn--active' : ''}`}
            onClick={() => { setDedupOpen(!dedupOpen); setDedupStatus(null); }}
            title="Bulk deduplicate"
          >
            🧹 Deduplicate {dedupOpen ? '▼' : '▶'}
          </button>
        </div>
      )}

      {/* Dedup options panel */}
      {dedupOpen && (
        <div className="inv-dedup-panel">
          <div className="inv-dedup-panel-row">
            <label className="inv-dedup-label">Keep:</label>
            <select
              className="inv-select inv-dedup-select"
              value={dedupStrategy}
              onChange={e => setDedupStrategy(e.target.value)}
            >
              <option value="keep_oldest">Oldest (original)</option>
              <option value="keep_newest">Newest (latest)</option>
            </select>
          </div>
          <div className="inv-dedup-panel-row">
            <label className="inv-dedup-check">
              <input
                type="checkbox"
                checked={dedupPreserveNames}
                onChange={e => setDedupPreserveNames(e.target.checked)}
              />
              Preserve files with different names
            </label>
          </div>
          <div className="inv-dedup-panel-row inv-dedup-actions">
            {selectedCount > 0 ? (
              <button
                className="btn btn-sm btn-primary"
                disabled={dedupBusy}
                onClick={async () => {
                  const ids = Array.from(state.selected);
                  if (!window.confirm(`Deduplicate within ${ids.length} selected file(s)?\n\nThis will delete duplicate copies, keeping the ${dedupStrategy === 'keep_oldest' ? 'oldest' : 'newest'} of each.`)) return;
                  setDedupBusy(true);
                  setDedupStatus(null);
                  try {
                    const res = await api.post(`/documents/case/${caseId}/bulk-deduplicate`, {
                      strategy: dedupStrategy,
                      document_ids: ids,
                      preserve_unique_names: dedupPreserveNames,
                    });
                    setDedupStatus({ text: `Removed ${res.deleted} duplicate(s) across ${res.groups_processed} group(s)`, error: false });
                    dispatch({ type: 'CLEAR_SELECTION' });
                    onDocChange?.();
                  } catch (err) {
                    setDedupStatus({ text: `Error: ${err.message}`, error: true });
                  } finally {
                    setDedupBusy(false);
                  }
                }}
              >
                {dedupBusy ? 'Working…' : `Dedup selected (${selectedCount})`}
              </button>
            ) : (
              <button
                className="btn btn-sm btn-primary"
                disabled={dedupBusy}
                onClick={async () => {
                  if (!window.confirm(`Deduplicate ALL files in this case?\n\nThis will delete duplicate copies across ${duplicateGroupCount} group(s), keeping the ${dedupStrategy === 'keep_oldest' ? 'oldest' : 'newest'} of each.${dedupPreserveNames ? '\n\nFiles with different names will be preserved.' : ''}`)) return;
                  setDedupBusy(true);
                  setDedupStatus(null);
                  try {
                    const res = await api.post(`/documents/case/${caseId}/bulk-deduplicate`, {
                      strategy: dedupStrategy,
                      preserve_unique_names: dedupPreserveNames,
                    });
                    setDedupStatus({ text: `Removed ${res.deleted} duplicate(s) across ${res.groups_processed} group(s)`, error: false });
                    onDocChange?.();
                  } catch (err) {
                    setDedupStatus({ text: `Error: ${err.message}`, error: true });
                  } finally {
                    setDedupBusy(false);
                  }
                }}
              >
                {dedupBusy ? 'Working…' : `Dedup all (${duplicateGroupCount} groups)`}
              </button>
            )}
            <button className="btn btn-sm" onClick={() => setDedupOpen(false)}>Cancel</button>
          </div>
          {dedupStatus && (
            <div className={`inv-dedup-status ${dedupStatus.error ? 'inv-dedup-status--error' : 'inv-dedup-status--ok'}`}>
              {dedupStatus.text}
            </div>
          )}
        </div>
      )}

      {/* Propagate action — only when there are un-acked corrections
          worth propagating. The "filter to corrected docs" affordance
          lives inside the Filters dropdown under Classification (option
          "✏️ Manually corrected") to keep the toolbar consistent with
          how every other filter axis is surfaced. */}
      {onPropagate && hasPendingPropagation && (
        <button
          className="inv-propagate-btn"
          onClick={onPropagate}
          title={`Propagate ${pendingCorrectionCount} pending correction${pendingCorrectionCount !== 1 ? 's' : ''} to similar documents`}
        >
          🤖 Propagate →
        </button>
      )}

      {/* Sort */}
      <button
        className="inv-sort-btn"
        onClick={() => dispatch({ type: 'SET_SORT', key: state.sortKey })}
        title={`Sort ${state.sortAsc ? 'ascending' : 'descending'}`}
      >
        {state.sortAsc ? '↑' : '↓'}
      </button>
      <select
        className="inv-select inv-select--sort"
        value={state.sortKey}
        onChange={e => dispatch({ type: 'SET_SORT', key: e.target.value })}
      >
        {Object.entries(SORT_LABELS).map(([k, label]) => (
          <option key={k} value={k}>{label}</option>
        ))}
      </select>

      <ColumnPicker />

      {/* View mode toggle */}
      <div className="inv-view-toggle">
        <button
          className={`inv-view-btn ${state.viewMode === VIEW_MODES.LIST ? 'inv-view-btn--active' : ''}`}
          onClick={() => dispatch({ type: 'SET_VIEW_MODE', mode: VIEW_MODES.LIST })}
          title="List view"
          data-view-mode="list"
        >
          ☰
        </button>
        <button
          className={`inv-view-btn ${state.viewMode === VIEW_MODES.GROUPED ? 'inv-view-btn--active' : ''}`}
          onClick={() => dispatch({ type: 'SET_VIEW_MODE', mode: VIEW_MODES.GROUPED })}
          title="Grouped by type"
          data-view-mode="grouped"
        >
          ▦
        </button>
        <button
          className={`inv-view-btn ${state.viewMode === VIEW_MODES.GRID ? 'inv-view-btn--active' : ''}`}
          onClick={() => dispatch({ type: 'SET_VIEW_MODE', mode: VIEW_MODES.GRID })}
          title="Cards — thumbnails with summary, group headers and details on click"
          data-view-mode="grid"
        >
          ▤
        </button>
      </div>

      <div className="inv-select-actions">
        <div className="inv-select-dropdown-container">
          <button
            className="btn btn-sm inv-select-dropdown-btn"
            type="button"
            onClick={() => setSelectDropdownOpen(!selectDropdownOpen)}
          >
            ☑ Select {selectDropdownOpen ? '▼' : '▶'}
          </button>
          {selectDropdownOpen && (
            <div className="inv-select-dropdown-menu">
              <button
                className="inv-select-dropdown-item"
                type="button"
                disabled={visibleDocIds.length === 0 || allVisibleSelected}
                onClick={() => {
                  dispatch({ type: 'SELECT_ALL', ids: visibleDocIds });
                  setSelectDropdownOpen(false);
                }}
              >
                📄 Select all in view ({visibleDocIds.length})
              </button>
              <button
                className="inv-select-dropdown-item"
                type="button"
                disabled={filteredDocIds.length === 0 || allFilteredSelected}
                onClick={() => {
                  dispatch({ type: 'SELECT_ALL', ids: filteredDocIds });
                  setSelectDropdownOpen(false);
                }}
              >
                📋 Select all ({filteredDocIds.length})
              </button>
              {selectedCount > 0 && (
                <>
                  <div className="inv-select-dropdown-divider" />
                  <button
                    className="inv-select-dropdown-item inv-select-dropdown-item--danger"
                    type="button"
                    onClick={() => {
                      dispatch({ type: 'CLEAR_SELECTION' });
                      setSelectDropdownOpen(false);
                    }}
                  >
                    Clear selection
                  </button>
                </>
              )}
            </div>
          )}
        </div>
        <span className="inv-select-count">{selectedCount} selected</span>
      </div>

      {canEscalateToFiltered && (
        <div className="inv-escalation">
          All {visibleDocIds.length} in view selected.
          <button
            className="btn btn-sm"
            type="button"
            onClick={() => dispatch({ type: 'SELECT_ALL', ids: filteredDocIds })}
          >
            Select all {filteredDocIds.length} filtered
          </button>
        </div>
      )}

      {/* Count */}
      <span className="inv-toolbar-count">
        {totalCount.toLocaleString()} {unit === UNITS.FILES ? 'file' : 'document'}{totalCount !== 1 ? 's' : ''}
      </span>
    </div>
  );
}
